import { useParams, Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState, useEffect } from 'react';
import { Bottles, Catalog, Wines, Labels, type Bottle, type BottleStatus, type WineLabelPanePayload } from '@/lib/api';
import { Button, Card, Icon, Input, Skeleton, Textarea, Badge, Spinner, Modal } from '@/design-system';
import { formatAbv, formatPriceNok, formatVolumeMl, formatDate } from '@/lib/format';
import { useToasts, usePrefs } from '@/lib/store';
import { AddBottleDrawer } from '@/components/AddBottleDrawer';
import { BottleImage } from '@/components/BottleCard';
import { CheckInModal } from '@/components/CheckInModal';

/**
 * URL `id` is either a real Bottle.id (UUID-ish) OR a catalog productId
 * (numeric string). We try Bottles.get(id) first; on 404 we fall back to the
 * catalog read.
 */
export function BottleDetailPage() {
  const { productId = '' } = useParams();
  const id = productId;
  const { t } = useTranslation();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { push } = useToasts();

  const bottleQ = useQuery({
    queryKey: ['bottle', id],
    queryFn: () => Bottles.get(id, 'catalog,notes'),
    enabled: Boolean(id),
    retry: false,
  });

  const isCellar = bottleQ.isSuccess;

  const catalogQ = useQuery({
    queryKey: ['catalog.product', id],
    queryFn: () => Catalog.product(id),
    enabled: Boolean(id) && bottleQ.isError,
  });

  const notesQ = useQuery({
    queryKey: ['bottle.notes', id],
    queryFn: () => Bottles.notes(id),
    enabled: isCellar,
  });

  const ratingsQ = useQuery({
    queryKey: ['wines.ratings', id],
    queryFn: () => Wines.getRatings(id),
    enabled: !isCellar && Boolean(id),
  });

  const [rating, setRating] = useState(0);
  const [notes, setNotes] = useState('');

  useEffect(() => {
    if (notesQ.data && notesQ.data.length > 0) {
      const last = notesQ.data[notesQ.data.length - 1]!;
      setRating(last.rating1to5);
      setNotes(last.comment ?? '');
    } else if (ratingsQ.data?.ratings) {
      const me = ratingsQ.data.ratings.find((r) => r.userId === 'dev-user');
      if (me) {
        setRating(me.rating);
        setNotes(me.notes ?? '');
      }
    }
  }, [notesQ.data, ratingsQ.data]);

  const saveNote = useMutation({
    mutationFn: async () => {
      if (isCellar) {
        await Bottles.addNote(id, { rating1to5: rating, comment: notes || undefined });
      } else {
        await Wines.upsertMyRating(id, { rating, notes: notes || undefined });
      }
    },
    onSuccess: () => {
      push({ title: t('bottle.saved'), tone: 'success' });
      void qc.invalidateQueries({ queryKey: ['bottle.notes', id] });
      void qc.invalidateQueries({ queryKey: ['wines.ratings', id] });
    },
    onError: (err: Error) =>
      push({ title: t('common.error'), description: err.message, tone: 'danger' }),
  });

  const drink = useMutation({
    mutationFn: () =>
      Bottles.drink(id, { drunkAt: new Date().toISOString(), rating1to5: rating || undefined, comment: notes || undefined }),
    onSuccess: () => {
      push({ title: 'Skål!', description: 'Flasken er markert som drukket.', tone: 'success' });
      void qc.invalidateQueries({ queryKey: ['cellar.bottles'] });
      navigate('/cellar');
    },
    onError: (err: Error) => push({ title: t('common.error'), description: err.message, tone: 'danger' }),
  });

  const remove = useMutation({
    mutationFn: () => Bottles.delete(id),
    onSuccess: () => {
      push({ title: 'Slettet', tone: 'success' });
      void qc.invalidateQueries({ queryKey: ['cellar.bottles'] });
      navigate('/cellar');
    },
    onError: (err: Error) => push({ title: t('common.error'), description: err.message, tone: 'danger' }),
  });

  const [confirmDelete, setConfirmDelete] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [labelOpen, setLabelOpen] = useState(false);
  const [checkInOpen, setCheckInOpen] = useState(false);

  // Pull the linked catalog product when a productId is set on the
  // bottle — gives us price, ABV, volume etc. without an extra Bottles call.
  const linkedProductId = bottleQ.data?.productId ?? bottleQ.data?.catalogId;
  const linkedCatalogQ = useQuery({
    queryKey: ['catalog.product', linkedProductId],
    queryFn: () => Catalog.product(linkedProductId!),
    enabled: Boolean(linkedProductId),
    staleTime: 30 * 60_000,
  });

  const monthlySalesQ = useQuery({
    queryKey: ['catalog.monthlySales', linkedProductId],
    queryFn: () => Catalog.monthlySales(linkedProductId!),
    enabled: Boolean(linkedProductId),
    staleTime: 60 * 60_000,
  });

  // X-Wines enrichment — extras Vinmonopolet doesn't carry (food pairings,
  // body, acidity, vintage history). Best-effort fuzzy match by name +
  // producer + country; silently absent when we can't find a confident hit.
  const enrichmentBasis = useMemo(() => {
    const b = bottleQ.data;
    const c = linkedCatalogQ.data?.product ?? catalogQ.data?.product;
    const name = b?.name ?? c?.basic?.productShortName ?? c?.basic?.productLongName;
    if (!name) return null;
    return {
      name,
      producer: b?.producer,
      country: b?.country,
      region: b?.region,
    };
  }, [bottleQ.data, catalogQ.data, linkedCatalogQ.data]);

  const enrichmentQ = useQuery({
    queryKey: ['catalog.enrichment', enrichmentBasis],
    queryFn: () => Catalog.enrichment(enrichmentBasis!),
    enabled: Boolean(enrichmentBasis),
    staleTime: 24 * 60 * 60_000,
    retry: false,
  });

  if (bottleQ.isLoading || (bottleQ.isError && catalogQ.isLoading)) {
    return (
      <div className="grid gap-6">
        <Skeleton height={28} width="200px" />
        <div className="grid gap-6 lg:grid-cols-[320px_1fr]">
          <Skeleton height={420} rounded="lg" />
          <div className="grid gap-3">
            <Skeleton height={24} width="60%" />
            <Skeleton height={16} width="50%" />
            <Skeleton height={120} />
          </div>
        </div>
      </div>
    );
  }

  if (bottleQ.isError && catalogQ.isError) {
    return <Card className="p-6">{t('common.error')}</Card>;
  }

  // ── Cellar bottle view ─────────────────────────────────────────────
  if (isCellar && bottleQ.data) {
    const b = bottleQ.data as Bottle;
    const archived = b.status !== 'cellared';
    return (
      <div className="grid gap-6">
        <div className="grid gap-3">
          <Link
            to={archived ? `/cellar?status=${b.status}` : '/cellar'}
            className="inline-flex items-center gap-2 text-sm text-mist hover:text-candle"
          >
            <Icon name="chevron-left" size={14} /> {t('bottle.backToCellar')}
          </Link>
          {/* Action bar — wraps cleanly on phone, single row on desktop. */}
          {archived ? (
            <Card className="flex flex-wrap items-center justify-between gap-3 p-4">
              <div className="grid gap-1">
                <span className="text-xs uppercase tracking-widest text-mist">Arkivert flaske</span>
                <span className="text-sm text-parchment">
                  Denne er markert som {statusLabel(b.status).toLowerCase()} og er ikke lenger aktiv i kjelleren.
                </span>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button variant="ghost" iconLeft="check-circle" onClick={() => setCheckInOpen(true)}>
                  Logg minne
                </Button>
                <Button variant="ghost" iconLeft="trash" onClick={() => setConfirmDelete(true)}>
                  Slett
                </Button>
              </div>
            </Card>
          ) : (
            <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap sm:justify-end">
              <Button
                variant="primary"
                iconLeft="check-circle"
                onClick={() => setCheckInOpen(true)}
                className="w-full sm:w-auto col-span-2"
              >
                Logg smaking
              </Button>
              <Button
                variant="ghost"
                iconLeft="edit"
                onClick={() => setEditOpen(true)}
                className="w-full sm:w-auto"
              >
                Rediger
              </Button>
              <Button
                variant="ghost"
                iconLeft="print"
                onClick={() => setLabelOpen(true)}
                className="w-full sm:w-auto"
              >
                Etikett
              </Button>
              <Button
                variant="ruby"
                iconLeft="bottle-empty"
                onClick={() => drink.mutate()}
                loading={drink.isPending}
                className="w-full sm:w-auto"
              >
                Drikk
              </Button>
              <Button
                variant="ghost"
                iconLeft="trash"
                onClick={() => setConfirmDelete(true)}
                className="w-full sm:w-auto"
              >
                Slett
              </Button>
            </div>
          )}
        </div>

        <div className="grid gap-6 md:grid-cols-[minmax(180px,240px)_1fr] xl:grid-cols-[minmax(220px,280px)_1fr] xl:gap-8">
          <div className="relative ui-card grid h-[16rem] place-items-center overflow-hidden p-4 sm:h-[17rem] md:h-[16rem] xl:h-[18rem]">
            <BottleImage src={b.photoUrl} alt={b.name} iconSize={88} mode="lightbox" />
          </div>

          <div className="grid gap-6">
            <div>
              <div className="mb-2 flex flex-wrap gap-2">
                {b.vintage && <Badge tone="ruby">{b.vintage}</Badge>}
                {b.country && <Badge tone="mist">{b.country}</Badge>}
                {b.tags.map((tag) => (
                  <Badge key={tag} tone="mist">{tag}</Badge>
                ))}
                {b.status !== 'cellared' && <Badge tone="danger">{statusLabel(b.status)}</Badge>}
              </div>
              <h1 className="font-serif text-4xl text-parchment">{b.name}</h1>
              {b.producer && <p className="text-sm text-mist">{b.producer}</p>}
              {b.region && <p className="mt-1 text-sm text-mist">{b.region}{b.country ? `, ${b.country}` : ''}</p>}
            </div>

            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <Stat label="Antall" value={`${b.qty} stk`} />
              <Stat label="Drikkes fra" value={b.drinkFromYear ? String(b.drinkFromYear) : '—'} />
              <Stat label="Drikkes til" value={b.drinkToYear ? String(b.drinkToYear) : '—'} />
              <Stat label="Lagt til" value={formatDate(b.addedAt)} />
            </div>

            {(b.drinkFromYear || b.drinkToYear) && (
              <DrinkWindowBar from={b.drinkFromYear} to={b.drinkToYear} />
            )}

            {b.grapeVarieties.length > 0 && (
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs uppercase tracking-widest text-mist">Druer:</span>
                {b.grapeVarieties.map((g) => <Badge key={g} tone="gold">{g}</Badge>)}
              </div>
            )}

            <BottleStatsCard
              bottle={b}
              catalog={linkedCatalogQ.data?.product}
              monthlySales={monthlySalesQ.data?.sales}
              tastingNotes={notesQ.data ?? []}
            />

            {b.notes && (
              <Card className="grid gap-2 p-4">
                <span className="text-xs uppercase tracking-widest text-mist">Notater</span>
                <p className="text-sm text-parchment">{b.notes}</p>
              </Card>
            )}

            <Card className="grid gap-3 p-5">
              <h3 className="ui-section-title text-xl">Smaks-notat</h3>
              <Stars value={rating} onChange={setRating} />
              <Textarea
                label="Kommentar"
                placeholder={t('bottle.placeholderNotes')}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
              <div className="flex items-center justify-end gap-2">
                <Button variant="primary" iconLeft="check" loading={saveNote.isPending} onClick={() => saveNote.mutate()}>
                  {t('common.save')}
                </Button>
              </div>
            </Card>

            {enrichmentQ.data && <EnrichmentCard data={enrichmentQ.data} />}

            {(notesQ.data?.length ?? 0) > 1 && (
              <Card className="grid gap-3 p-5">
                <h3 className="ui-section-title text-xl">Tidligere smaks-notater</h3>
                <ul className="grid gap-2">
                  {notesQ.data!.slice(0, -1).map((n) => (
                    <li key={n.id} className="rounded-md border border-line p-3 text-sm">
                      <div className="mb-1 flex items-center gap-2">
                        <Stars value={n.rating1to5} readOnly size={14} />
                        <span className="text-xs text-mist">{formatDate(n.drunkAt ?? n.createdAt)}</span>
                      </div>
                      {n.comment && <p className="text-parchment-dim">{n.comment}</p>}
                    </li>
                  ))}
                </ul>
              </Card>
            )}
          </div>
        </div>

        <Modal open={confirmDelete} onClose={() => setConfirmDelete(false)} title="Slette flasken?" size="sm">
          <p className="mb-4 text-sm text-mist">Dette kan ikke angres. Flasken og alle smaks-notater fjernes.</p>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setConfirmDelete(false)}>Avbryt</Button>
            <Button variant="ruby" iconLeft="trash" onClick={() => remove.mutate()} loading={remove.isPending}>
              Slett
            </Button>
          </div>
        </Modal>

        <AddBottleDrawer open={editOpen} onClose={() => setEditOpen(false)} existing={b} />

        <QuickLabelPrintModal
          open={labelOpen}
          onClose={() => setLabelOpen(false)}
          bottle={b}
          catalog={linkedCatalogQ.data?.product}
        />

        <CheckInModal
          open={checkInOpen}
          onClose={() => setCheckInOpen(false)}
          bottleId={b.id}
          productId={b.productId}
          bottleName={b.name}
          imageUrl={b.photoUrl}
        />
      </div>
    );
  }

  // ── Catalog view fallback ──────────────────────────────────────────
  if (catalogQ.data) {
    const p = catalogQ.data.product;
    const name = p.basic.productLongName ?? p.basic.productShortName ?? '—';
    const country = (p.origins as { country?: string } | undefined)?.country;
    const region = (p.origins as { region?: string } | undefined)?.region;
    const price = (p.prices as { salesPrice?: number } | undefined)?.salesPrice;
    const wineType = (p.classification as { mainCategory?: { code?: string; name?: string } } | undefined)?.mainCategory?.name;

    return (
      <div className="grid gap-8">
        <Link to="/cellar" className="inline-flex items-center gap-2 text-sm text-mist hover:text-candle">
          <Icon name="chevron-left" size={14} /> {t('bottle.backToCellar')}
        </Link>

        <div className="grid gap-8 md:grid-cols-[minmax(180px,240px)_1fr] xl:grid-cols-[minmax(220px,280px)_1fr]">
          <div className="relative ui-card grid h-[16rem] place-items-center overflow-hidden p-4 sm:h-[17rem] md:h-[16rem] xl:h-[18rem]">
            <BottleImage src={p.imageUrl} alt={name} iconSize={88} mode="lightbox" />
          </div>

          <div className="grid gap-6">
            <div>
              <div className="mb-2 flex flex-wrap gap-2">
                {wineType && <Badge tone="gold">{wineType}</Badge>}
                {p.basic.vintage && <Badge tone="ruby">{p.basic.vintage}</Badge>}
                {country && <Badge tone="mist">{country}</Badge>}
                <Badge tone="mist">Katalog</Badge>
              </div>
              <h1 className="font-serif text-4xl text-parchment">{name}</h1>
              {region && <p className="mt-1 text-sm text-mist">{region}{country ? `, ${country}` : ''}</p>}
            </div>

            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <Stat label="Pris" value={formatPriceNok(price)} />
              <Stat label="Volum" value={formatVolumeMl(p.basic.volume)} />
              <Stat label="Alkohol" value={formatAbv(p.basic.alcoholContent)} />
              <Stat label="Katalog-ID" value={`#${p.basic.productId}`} />
            </div>

            <Card className="grid gap-3 p-5">
              <p className="text-sm text-parchment">
                Denne flasken er ikke i kjelleren din enda.
              </p>
              <Button
                variant="primary"
                iconLeft="add-bottle"
                onClick={async () => {
                  try {
                    const b = await Bottles.create({
                      productId: p.basic.productId,
                      catalogId: p.basic.productId,
                      name,
                      vintage: p.basic.vintage,
                      country,
                      region,
                      photoUrl: p.imageUrl,
                      qty: 1,
                    });
                    push({ title: 'Lagt i kjelleren', tone: 'success' });
                    navigate(`/bottle/${b.id}`);
                  } catch (e) {
                    push({ title: 'Feil', description: (e as Error).message, tone: 'danger' });
                  }
                }}
              >
                Legg i min kjeller
              </Button>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  return null;
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="ui-card p-3">
      <div className="text-[10px] uppercase tracking-widest text-mist">{label}</div>
      <div className="font-serif text-lg text-parchment">{value}</div>
    </div>
  );
}

function EnrichmentCard({
  data,
}: {
  data: import('@/lib/api').CatalogEnrichment;
}) {
  const meta = [data.type, data.body, data.acidity ? `${data.acidity} syre` : null]
    .filter(Boolean)
    .join(' · ');
  const numericVintages = data.vintages
    .filter((v): v is number => typeof v === 'number')
    .sort((a, b) => b - a);
  const vintageLabel =
    numericVintages.length > 0
      ? `${numericVintages[0]}${numericVintages.length > 1 ? `–${numericVintages[numericVintages.length - 1]}` : ''}`
      : data.vintages.length > 0
        ? data.vintages.join(', ')
        : null;
  return (
    <Card className="grid gap-3 p-5">
      <div className="flex items-center justify-between gap-2">
        <h3 className="ui-section-title text-xl">Mat &amp; karakter</h3>
        <Badge tone="mist">X-Wines</Badge>
      </div>
      {meta && <p className="text-xs uppercase tracking-widest text-mist">{meta}</p>}
      {data.foodPairings.length > 0 && (
        <div className="grid gap-2">
          <div className="text-[10px] uppercase tracking-widest text-mist">Passer til</div>
          <div className="flex flex-wrap gap-1.5">
            {data.foodPairings.map((pairing) => (
              <Badge key={pairing} tone="gold">
                {pairing}
              </Badge>
            ))}
          </div>
        </div>
      )}
      {data.grapes.length > 0 && (
        <div className="grid gap-2">
          <div className="text-[10px] uppercase tracking-widest text-mist">Druer</div>
          <div className="flex flex-wrap gap-1.5">
            {data.grapes.map((grape) => (
              <Badge key={grape} tone="ruby">
                {grape}
              </Badge>
            ))}
          </div>
        </div>
      )}
      {vintageLabel && (
        <p className="text-xs text-mist">
          Kjente årganger: <span className="text-parchment-dim">{vintageLabel}</span>
        </p>
      )}
      <p className="text-[10px] text-mist/70">
        Data fra X-Wines · CC-BY-NC-SA-4.0
      </p>
    </Card>
  );
}

function Stars({ value, onChange, readOnly, size = 26 }: { value: number; onChange?: (v: number) => void; readOnly?: boolean; size?: number }) {
  const max = 5;
  const normalized = clampRating(value);
  return (
    <div className="flex flex-wrap items-center gap-2">
      {Array.from({ length: max }).map((_, i) => {
        const fillPct = Math.max(0, Math.min(1, normalized - i)) * 100;
        if (readOnly) {
          return (
            <span key={i} className="relative inline-grid" style={{ width: size, height: size }}>
              <Icon name="star" size={size} className="absolute inset-0 text-mist/40" />
              {fillPct > 0 && (
                <span aria-hidden className="absolute inset-0 overflow-hidden" style={{ width: `${fillPct}%` }}>
                  <Icon name="star" size={size} className="text-candle drop-shadow-[0_0_8px_rgba(230,180,107,0.4)]" />
                </span>
              )}
            </span>
          );
        }
        return (
          <button
            key={i}
            type="button"
            onClick={() => onChange?.(i + 1)}
            aria-label={`${i + 1} stjerner`}
            className="relative inline-grid transition-transform hover:scale-110"
            style={{ width: size, height: size }}
          >
            <Icon name="star" size={size} className="absolute inset-0 text-mist/40" />
            {fillPct > 0 && (
              <span aria-hidden className="absolute inset-0 overflow-hidden" style={{ width: `${fillPct}%` }}>
                <Icon name="star" size={size} className="text-candle drop-shadow-[0_0_8px_rgba(230,180,107,0.4)]" />
              </span>
            )}
          </button>
        );
      })}
      {!readOnly && (
        <label className="ml-1 inline-flex items-center gap-2 text-sm text-mist">
          <input
            type="number"
            min={0}
            max={5}
            step={0.1}
            inputMode="decimal"
            value={normalized}
            onChange={(e) => onChange?.(clampRating(Number(e.target.value)))}
            className="h-9 w-20 rounded-md border border-line bg-ink-soft px-2 text-sm text-parchment focus:border-candle focus:outline-none"
            aria-label="Vurdering fra 0 til 5"
          />
          / 5
        </label>
      )}
    </div>
  );
}

function clampRating(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.max(0, Math.min(5, Math.round(value * 10) / 10));
}

function statusLabel(status: BottleStatus): string {
  switch (status) {
    case 'drunk': return 'Drukket';
    case 'gifted': return 'Bortgitt';
    case 'cellared': return 'I kjelleren';
  }
}

/**
 * Statistics card — surfaces everything we know about this wine in one place:
 * household tasting history, catalog popularity (monthly sales), price
 * and physical attributes from the linked catalog product.
 */
function BottleStatsCard({
  bottle,
  catalog,
  monthlySales,
  tastingNotes,
}: {
  bottle: Bottle;
  catalog?: import('@/lib/api').VinmonopoletProduct;
  monthlySales?: import('@/lib/api').ProductSalesSummary | { months: import('@/lib/api').MonthlyProductSales[] };
  tastingNotes: import('@/lib/api').TastingNote[];
}) {
  const avgRating = useMemo(() => {
    if (tastingNotes.length === 0) return null;
    const sum = tastingNotes.reduce((s, n) => s + (n.rating1to5 ?? 0), 0);
    return sum / tastingNotes.length;
  }, [tastingNotes]);

  const drunkCount = tastingNotes.filter((n) => n.drunkAt).length;
  const price = (catalog?.prices as { salesPrice?: number } | undefined)?.salesPrice;
  const wineType = (catalog?.classification as { mainCategory?: { name?: string } } | undefined)?.mainCategory
    ?.name;

  // Sales sparkline — last 12 months, normalised
  const salesMonths = (monthlySales as { months?: import('@/lib/api').MonthlyProductSales[] } | undefined)
    ?.months ?? [];
  const sortedSales = [...salesMonths].sort((a, b) => a.salesMonth.localeCompare(b.salesMonth)).slice(-12);
  const maxQty = sortedSales.reduce((m, x) => Math.max(m, x.salesQuantity), 0);

  const hasTasting = avgRating != null || drunkCount > 0;
  const hasCatalog = price != null || wineType != null || catalog?.basic.alcoholContent != null || catalog?.basic.volume != null;
  const hasSales = sortedSales.length > 0;

  return (
    <Card className="grid gap-4 p-5">
      <div className="flex items-center gap-3">
        <Icon name="sparkles" className="text-candle" />
        <h3 className="ui-section-title text-xl">Statistikk</h3>
      </div>

      {(hasTasting || hasCatalog) ? (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          {avgRating != null ? (
            <Stat
              label={`Snitt (${tastingNotes.length} ${tastingNotes.length === 1 ? 'notat' : 'notater'})`}
              value={`${avgRating.toFixed(1)} / 5`}
            />
          ) : (
            <Stat label="Snitt" value="—" />
          )}
          <Stat
            label="Drukket"
            value={drunkCount > 0 ? `${drunkCount} ${drunkCount === 1 ? 'gang' : 'ganger'}` : '—'}
          />
          <Stat label="Pris" value={price != null ? formatPriceNok(price) : '—'} />
          <Stat
            label="Alkohol"
            value={catalog?.basic.alcoholContent != null ? formatAbv(catalog.basic.alcoholContent) : '—'}
          />
          <Stat
            label="Volum"
            value={catalog?.basic.volume != null ? formatVolumeMl(catalog.basic.volume) : '—'}
          />
          <Stat label="Type" value={wineType ?? '—'} />
        </div>
      ) : (
        <p className="text-sm text-mist">
          Ingen smaks-notater eller katalogdata enda.
        </p>
      )}

      {hasSales && (
        <div>
          <div className="mb-2 flex items-center justify-between text-xs uppercase tracking-widest text-mist">
            <span>Katalogpopularitet</span>
            <span>siste {sortedSales.length} mnd</span>
          </div>
          <div className="flex h-12 items-end gap-1">
            {sortedSales.map((m) => {
              const pct = maxQty > 0 ? (m.salesQuantity / maxQty) * 100 : 0;
              return (
                <div
                  key={m.salesMonth}
                  className="flex-1 rounded-t bg-gradient-to-t from-ruby/40 to-candle/80"
                  style={{ height: `${Math.max(4, pct)}%` }}
                  title={`${m.salesMonth}: ${m.salesQuantity}`}
                />
              );
            })}
          </div>
        </div>
      )}

      {!catalog && bottle.productId && (
        <p className="text-xs text-mist">
          Henter katalogdata for dette produktet…
        </p>
      )}
      {!catalog && !bottle.productId && (
        <p className="text-xs text-mist">
          Koble til et katalogprodukt (rediger flasken og søk i katalogen) for å se pris,
          alkohol, volum og popularitet.
        </p>
      )}
      {!hasTasting && (
        <p className="text-xs text-mist">
          Skriv et smaks-notat under for å bygge opp historikk og snitt.
        </p>
      )}
    </Card>
  );
}

/**
 * Quick label print modal — pre-fills label fields from the bottle data,
 * lets the user adjust quantity, and prints in one tap. For full control
 * (multi-pane row, debug frame, custom occasion text) link to /labels.
 */
function QuickLabelPrintModal({
  open,
  onClose,
  bottle,
  catalog,
}: {
  open: boolean;
  onClose: () => void;
  bottle: Bottle;
  catalog?: import('@/lib/api').VinmonopoletProduct;
}) {
  const { push } = useToasts();
  const { printTarget } = usePrefs();
  const navigate = useNavigate();

  const initial = useMemo<WineLabelPanePayload>(() => {
    const fromCatalog = catalog?.description as
      | { characteristics?: { fitsTo?: string[] } }
      | undefined;
    const wineType = (catalog?.classification as
      | { mainCategory?: { code?: string } }
      | undefined)?.mainCategory?.code;
    const fromMetaPairings = ((bottle.metadata as Record<string, unknown> | undefined)
      ?.foodPairings as string[] | undefined) ?? [];
    const fromMetaOccasion = ((bottle.metadata as Record<string, unknown> | undefined)
      ?.occasion as string | undefined) ?? undefined;
    return {
      productName: bottle.name,
      producer: bottle.producer,
      vintage: bottle.vintage,
      country: bottle.country,
      region: bottle.region,
      typeCode: wineType ?? undefined,
      foodPairings: fromMetaPairings.length > 0 ? fromMetaPairings : fromCatalog?.characteristics?.fitsTo,
      occasion: fromMetaOccasion,
      barcode: bottle.barcode,
    };
  }, [bottle, catalog]);

  const [pane, setPane] = useState<WineLabelPanePayload>(initial);
  const [quantity, setQuantity] = useState(1);

  // Reset on open
  useEffect(() => {
    if (open) {
      setPane(initial);
      setQuantity(1);
    }
  }, [open, initial]);

  const create = useMutation({
    mutationFn: () =>
      Labels.create({
        ...pane,
        quantity,
        printerName: printTarget?.printerName,
        deliveryTarget: printTarget ?? undefined,
      }),
    onSuccess: () => {
      push({ title: 'Etikett sendt til kø', tone: 'success' });
      onClose();
    },
    onError: (err: Error) => push({ title: 'Print feilet', description: err.message, tone: 'danger' }),
  });

  return (
    <Modal open={open} onClose={onClose} title="Print etikett" size="md">
      <div className="grid gap-4">
        {/* Visual preview — what the label will say */}
        <div
          className="grid place-items-center rounded border border-line p-4"
          style={{
            background:
              'linear-gradient(180deg, #f5efe2 0%, #e8dfc8 100%)',
            color: '#3a2e1f',
          }}
        >
          <div className="text-center">
            <div className="font-serif text-lg leading-tight">
              {pane.productName ?? '—'}
            </div>
            {pane.cuvee && <div className="text-xs italic">{pane.cuvee}</div>}
            <div className="mt-1 text-xs">
              {[pane.region, pane.country, pane.vintage].filter(Boolean).join(' · ')}
            </div>
            {pane.foodPairings && pane.foodPairings.length > 0 && (
              <div className="mt-2 text-[10px] uppercase tracking-widest">
                {pane.foodPairings.slice(0, 3).join(' · ')}
              </div>
            )}
            {pane.occasion && (
              <div className="mt-1 text-[10px] uppercase tracking-widest">
                {pane.occasion}
              </div>
            )}
          </div>
        </div>

        {/* Editable basics */}
        <div className="grid gap-3 sm:grid-cols-2">
          <Input
            label="Navn"
            value={pane.productName ?? ''}
            onChange={(e) => setPane((p) => ({ ...p, productName: e.target.value }))}
          />
          <Input
            label="Cuvée"
            value={pane.cuvee ?? ''}
            onChange={(e) => setPane((p) => ({ ...p, cuvee: e.target.value }))}
          />
          <Input
            label="Anledning"
            value={pane.occasion ?? ''}
            onChange={(e) => setPane((p) => ({ ...p, occasion: e.target.value }))}
          />
          <Input
            label="Antall etiketter"
            type="number"
            min={1}
            max={50}
            value={quantity}
            onChange={(e) => setQuantity(Math.max(1, Number(e.target.value) || 1))}
          />
        </div>
        <Input
          label="Matparing (komma-separert)"
          value={pane.foodPairings?.join(', ') ?? ''}
          onChange={(e) =>
            setPane((p) => ({
              ...p,
              foodPairings: e.target.value
                .split(',')
                .map((s) => s.trim())
                .filter(Boolean),
            }))
          }
        />

        <div className="flex flex-wrap items-center justify-between gap-2 pt-2">
          <button
            type="button"
            onClick={() => {
              onClose();
              navigate('/labels');
            }}
            className="text-xs uppercase tracking-widest text-mist hover:text-candle"
          >
            Avansert editor →
          </button>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={onClose}>
              Avbryt
            </Button>
            <Button
              variant="primary"
              iconLeft="print"
              loading={create.isPending}
              onClick={() => create.mutate()}
            >
              Print {quantity} {quantity === 1 ? 'etikett' : 'etiketter'}
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
}

function DrinkWindowBar({ from, to }: { from?: number; to?: number }) {
  const now = new Date().getFullYear();
  if (!from && !to) return null;
  const start = from ?? now;
  const end = to ?? now + 5;
  const total = end - start || 1;
  const pos = Math.max(0, Math.min(1, (now - start) / total));
  const inWindow = now >= start && now <= end;
  return (
    <Card className="grid gap-2 p-4">
      <div className="flex items-center justify-between text-xs uppercase tracking-widest text-mist">
        <span>{start}</span>
        <span className={inWindow ? 'text-candle' : 'text-mist'}>{inWindow ? 'I drikkevindu' : now < start ? 'For tidlig' : 'For sent'}</span>
        <span>{end}</span>
      </div>
      <div className="relative h-2 rounded-full bg-ink-soft">
        <div className="absolute inset-y-0 left-0 rounded-full bg-gradient-to-r from-ruby to-candle" style={{ width: `${pos * 100}%` }} />
        <div
          className="absolute -top-1 h-4 w-1 rounded-full bg-candle shadow-[0_0_12px_rgba(230,180,107,0.6)]"
          style={{ left: `calc(${pos * 100}% - 2px)` }}
          aria-label="Nåværende år"
        />
      </div>
    </Card>
  );
}
